import type { ReactNode } from "react";

import { Avatar, type AvatarSize } from "./Avatar.js";
import { cn } from "../lib/cn.js";

/**
 * Several faces, overlapping — for a record that belongs to more than one person.
 *
 * ==========================================================================================
 * WHY THIS EXISTS, AND WHAT IT CORRECTS
 * ==========================================================================================
 *
 * "Joint" was rendering an empty box the same size as an avatar. That held the column's alignment,
 * which was the whole of the reasoning, and it said the wrong thing: a joint account is not a row
 * with NOBODY in it, it is a row with EVERYBODY in it. The empty circle read as a member whose
 * picture had failed to load, and the gap beside the word was the visual admission that nothing was
 * being expressed.
 *
 * The distinction that survives is the one that matters. "Daily update" and "A former member" still
 * get nothing at all, because those genuinely have no person behind them — an overnight fetch and a
 * deleted account. Only a household-owned record gets the group.
 *
 * ==========================================================================================
 * THE OVERLAP, AND THE RIM THAT MAKES IT LEGIBLE
 * ==========================================================================================
 *
 * Each face is drawn over the one before it, which is what says "these people together" rather than
 * "these people in a list". Without a rim the shapes merge into one blob at 24px, so every avatar
 * already carries `ring-1 ring-line` and the overlap relies on it.
 *
 * FIRST FACE FIRST, in reading order, with the stacking left as the DOM gives it — later siblings
 * paint on top. HeroUI reverses this so the first avatar sits above the rest; here the household
 * has no ordering worth asserting, and reversing would make the visual order disagree with the
 * order a screen reader would meet if these were ever announced.
 */

export interface AvatarGroupPerson {
  /** For the initials, and for the title. The full name — shortening happens at the label. */
  name: string;
  avatarUrl?: string | null | undefined;
}

export interface AvatarGroupProps {
  people: readonly AvatarGroupPerson[];
  /**
   * How many faces before the rest become a count.
   *
   * Four by default, which no household in this product will reach — the constitution caps the
   * scope at one family. It exists so a group cannot silently become a row of twelve circles if
   * that assumption ever changes, rather than because anybody has hit it.
   */
  max?: number;
  size?: AvatarSize;
  className?: string;
  testId?: string;
}

/** Matches the size of the avatars it sits beside, so the row's height does not depend on it. */
const COUNTER: Record<AvatarSize, string> = {
  sm: "size-6 text-[0.625rem]",
  md: "size-8 text-xs",
  lg: "size-24 text-2xl",
};

export function AvatarGroup({
  people,
  max = 4,
  size = "sm",
  className,
  testId,
}: AvatarGroupProps): ReactNode {
  const shown = people.slice(0, max);
  const hidden = people.length - shown.length;

  return (
    <span
      {...(testId === undefined ? {} : { "data-testid": testId })}
      className={cn("inline-flex shrink-0 items-center", className)}
      title={people.map((person) => person.name).join(", ")}
    >
      {shown.map((person, index) => (
        <Avatar
          key={person.name}
          src={person.avatarUrl ?? null}
          name={person.name}
          size={size}
          // Every face after the first is pulled onto the one before it. `-ml-2` at `sm` leaves
          // roughly a third of each circle covered, which reads as a stack without hiding a face.
          // Spread rather than `undefined`: `exactOptionalPropertyTypes` treats an explicit
          // undefined as different from an omitted prop.
          {...(index === 0 ? {} : { className: "-ml-2" })}
        />
      ))}

      {hidden > 0 ? (
        <span
          aria-hidden="true"
          className={cn(
            "-ml-2 inline-flex shrink-0 items-center justify-center rounded-full",
            "bg-secondary font-medium text-muted ring-1 ring-line",
            COUNTER[size],
          )}
        >
          +{hidden}
        </span>
      ) : null}
    </span>
  );
}
